(function () {
  "use strict";

  class ProductCardBadgesComponent extends HTMLElement {
    constructor() {
      super();
      this._isConnected = false;
      this._needsRender = false;
      this._renderRAF = null;
      this._lastHTML = null;
      this._observer = null;
    }

    static get observedAttributes() {
      return ["product"];
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      const isOnSale = this._checkIfOnSale(value);
      if (this._isConnected) {
        if (isOnSale) {
          this._scheduleRender();
        } else {
          this.innerHTML = "";
          this._lastHTML = null;
          this._needsRender = false;
        }
      } else {
        this._needsRender = isOnSale;
      }
    }

    connectedCallback() {
      // Check if product is set via attribute
      const productAttr = this.getAttribute("product");
      if (productAttr && !this._product) {
        try {
          const parsed = JSON.parse(productAttr);
          this.product = parsed;
        } catch (e) {
          // ignore invalid JSON
        }
      }
      
      this._isConnected = true;
      
      // Set up MutationObserver to watch for attribute changes
      if (!this._observer && typeof MutationObserver !== "undefined") {
        this._observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === "attributes" && mutation.attributeName === "product") {
              const newAttr = this.getAttribute("product");
              if (newAttr && !this._product) {
                try {
                  const parsed = JSON.parse(newAttr);
                  this.product = parsed;
                } catch (e) {
                  // ignore invalid JSON
                }
              }
            }
          });
        });
        this._observer.observe(this, {
          attributes: true,
          attributeFilter: ["product"]
        });
      }
      
      // If we have product data now, try to render
      if (this._product) {
        const isOnSale = this._checkIfOnSale(this._product);
        if (isOnSale) {
          this._scheduleRender();
          this._needsRender = false;
        }
      } else if (this._needsRender) {
        this._scheduleRender();
        this._needsRender = false;
      } else {
        // Set up a check for product being set later (in case it's set as a property)
        setTimeout(() => {
          if (!this._product && !this._lastHTML) {
            const productAttrLater = this.getAttribute("product");
            if (productAttrLater && !this._product) {
              try {
                const parsed = JSON.parse(productAttrLater);
                this.product = parsed;
              } catch (e) {
                // ignore invalid JSON
              }
            }
          }
        }, 100);
      }
    }

    disconnectedCallback() {
      this._isConnected = false;
      if (this._renderRAF) {
        cancelAnimationFrame(this._renderRAF);
        this._renderRAF = null;
      }
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "product" && newVal && newVal !== oldVal) {
        try {
          const parsed = JSON.parse(newVal);
          this.product = parsed;
        } catch (e) {
          // ignore invalid JSON
        }
      }
    }

    _checkIfOnSale(product) {
      if (!product) return false;
      
      // Check priceInfo structure (from RCC search)
      if (product.priceInfo) {
        const originalPrice = parseFloat(product.priceInfo.originalPrice ?? 0);
        const currentPrice = parseFloat(product.priceInfo.price ?? product.priceInfo.currentPrice ?? 0);
        
        if (originalPrice > 0 && currentPrice > 0 && originalPrice > currentPrice) {
          return true;
        }
      }
      
      // Fallback: check compare_at_price vs price (standard Shopify structure)
      if (product.compare_at_price && product.price) {
        const compareAtPrice = parseFloat(product.compare_at_price);
        const price = parseFloat(product.price);
        if (compareAtPrice > price) {
          return true;
        }
      }
      
      // Check variants for sale
      if (Array.isArray(product.variants) && product.variants.length > 0) {
        return product.variants.some(variant => {
          const compareAtPrice = parseFloat(variant.compare_at_price ?? 0);
          const price = parseFloat(variant.price ?? 0);
          return compareAtPrice > 0 && price > 0 && compareAtPrice > price;
        });
      }
      
      return false;
    }

    _scheduleRender() {
      if (this._renderRAF) cancelAnimationFrame(this._renderRAF);
      this._renderRAF = requestAnimationFrame(() => {
        this._renderRAF = null;
        this._doRender();
      });
    }

    _doRender() {
      if (!this._product) {
        this.innerHTML = "";
        this._lastHTML = null;
        return;
      }
      
      const isOnSale = this._checkIfOnSale(this._product);
      
      if (!isOnSale) {
        this.innerHTML = "";
        this._lastHTML = null;
        return;
      }

      // Generate badge HTML
      const badgeHTML = `
        <rcc-product-card-badges class="rcc-product-card__badge rcc-product-card__badge--sale">
          Sale
        </rcc-product-card-badges>
      `;

      if (badgeHTML !== this._lastHTML) {
        this.innerHTML = badgeHTML;
        this._lastHTML = badgeHTML;
      }
    }
  }

  customElements.define("rcc-product-card-badges", ProductCardBadgesComponent);
})();

